import { Button } from '@amzn/awsui-components-react';
import React, { useContext, useEffect, useState } from 'react';
import { isPiezoEnabledEnvironment } from '../../../client/piezo/signing/environment';
import { refreshPiezoMaterial } from '../../../client/piezo/provisioning/refreshPiezoMaterial';
import { isPiezoInitInProgress, onPiezoInitProgressChange } from '../../../client/piezo/provisioning/initStatus';
import { isMidwayAuthError, MIDWAY_LOGIN_LABEL } from '../../../client/piezo/signing/midwaySession';
import { useToastNotifications } from '../../../hooks/useToastList';
import { AppContext } from '../../../utils/appContext';

import { PIEZO_REFRESH_LABEL } from '../../../client/piezo/provisioning/PiezoClient.constants';

const REFRESH_FAILED = 'Piezo refresh failed';

/**
 * A refresh cannot fix an expired Midway session -- the re-provision it runs is itself Midway
 * authenticated -- so point at the one control that can. Named by the constant the button renders.
 */
const failureMessage = (detail: string): string =>
  isMidwayAuthError(detail)
    ? `${REFRESH_FAILED}: ${detail}. Click the "${MIDWAY_LOGIN_LABEL}" button in the navbar.`
    : `${REFRESH_FAILED}: ${detail}. Try again, or reload the page.`;

/**
 * Re-provisions the operator's Piezo material on demand, for when the cached material is stale rather than
 * missing, and shows while Piezo init is fetching its material. Renders only where Piezo runs.
 *
 * Reports through a toast, which requires TumblerHome's ToastList to wrap the navbar -- it used to sit
 * below it, and the empty default context made addDanger throw on the outcome it was reporting.
 */
export const PiezoRefreshButton = () => {
  const appContext = useContext(AppContext);
  const toasts = useToastNotifications();
  const [refreshing, setRefreshing] = useState(false);
  const [initializing, setInitializing] = useState(isPiezoInitInProgress());

  useEffect(() => {
    const sync = () => setInitializing(isPiezoInitInProgress());
    const stop = onPiezoInitProgressChange(sync);
    // Init may have started between the first render and subscribing.
    sync();
    return stop;
  }, []);

  if (!isPiezoEnabledEnvironment()) {
    return null;
  }

  const onClick = async () => {
    setRefreshing(true);
    try {
      const result = await refreshPiezoMaterial(appContext.region.publicRegion);
      if (result.error) {
        toasts.addDanger(failureMessage(result.error.message));
      } else {
        toasts.addSuccess('Piezo refreshed. Reload the page to retry anything that failed.');
      }
    } catch (err) {
      // refreshPiezoMaterial returns its failure, so reaching here is unexpected.
      toasts.addDanger(failureMessage(String(err)));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Button
      iconName="refresh"
      variant="normal"
      loading={refreshing || initializing}
      // Both run the full cold path, so name it rather than showing a bare spinner.
      loadingText={refreshing ? 'Refreshing Piezo' : 'Initializing Piezo'}
      onClick={onClick}
    >
      {PIEZO_REFRESH_LABEL}
    </Button>
  );
};
